import {
  type AssessmentKind,
  type GradeValue,
  NATIONAL_RULES_2026,
  type ScaleCode,
  type SpecialMark,
  resolveRule,
} from '@shkola/core';
import type { Database } from '@shkola/db-types';
import { useMutation, useMutationState, useQuery, useQueryClient } from '@tanstack/react-query';
import { classLabel } from './contexts';
import { type Db, must, useDb } from './db';
import {
  type JournalAbsenceAction,
  type JournalConflictError,
  type JournalGradeAction,
  OFFLINE_ACTIONS,
  isJournalConflict,
  useQueuedActions,
} from './offline';

export type AbsenceMark = Database['public']['Enums']['absence_mark'];
export type GradeRow = Database['public']['Tables']['grades']['Row'];
export type AttendanceRow = Database['public']['Tables']['attendance']['Row'];
export type FormativeRow = Database['public']['Tables']['formative_marks']['Row'];

export interface CourseRef {
  id: string;
  subject: string;
  groupLabel: string | null;
  classId: string;
  classLabel: string;
  grade: number;
}

export interface JournalLesson {
  id: string;
  date: string;
  period: number;
  topic: string | null;
  bring: string | null;
  isTest: boolean;
  format: Database['public']['Enums']['lesson_format'];
  status: Database['public']['Enums']['lesson_status'];
  startsAt: string;
  endsAt: string;
}

export interface JournalStudent {
  id: string;
  firstName: string;
  lastName: string;
}

export interface Journal {
  course: CourseRef;
  termId: string;
  students: JournalStudent[];
  lessons: JournalLesson[];
  grades: GradeRow[];
  attendance: AttendanceRow[];
  formative: FormativeRow[];
}

const COURSE_SELECT = `
  id, group_label,
  subject:subjects!courses_school_id_subject_id_fkey(name),
  class:classes!courses_school_id_class_id_fkey(id, grade, letter)
`;

function toCourseRef(c: {
  id: string;
  group_label: string | null;
  subject: { name: string };
  class: { id: string; grade: number; letter: string };
}): CourseRef {
  return {
    id: c.id,
    subject: c.subject.name,
    groupLabel: c.group_label,
    classId: c.class.id,
    classLabel: classLabel(c.class),
    grade: c.class.grade,
  };
}

const byName = (a: JournalStudent, b: JournalStudent) =>
  a.lastName.localeCompare(b.lastName, 'uk') || a.firstName.localeCompare(b.firstName, 'uk');

/** Courses a teacher teaches, or all courses of a class (homeroom teacher, administration). */
export function useCourses(filter: { teacherMemberId: string } | { classId: string }) {
  const db = useDb();
  return useQuery({
    queryKey: ['courses', filter],
    enabled: 'teacherMemberId' in filter ? filter.teacherMemberId !== '' : filter.classId !== '',
    queryFn: async () => {
      let ids: string[] | undefined;
      if ('teacherMemberId' in filter) {
        ids = must(
          await db
            .from('course_teachers')
            .select('course_id')
            .eq('teacher_member_id', filter.teacherMemberId),
        ).map((r) => r.course_id);
      }
      let query = db.from('courses').select(COURSE_SELECT);
      query = ids
        ? query.in('id', ids)
        : query.eq('class_id', (filter as { classId: string }).classId);
      return must(await query)
        .map(toCourseRef)
        .sort(
          (a, b) =>
            a.grade - b.grade ||
            a.classLabel.localeCompare(b.classLabel, 'uk') ||
            a.subject.localeCompare(b.subject, 'uk') ||
            (a.groupLabel ?? '').localeCompare(b.groupLabel ?? ''),
        );
    },
  });
}

/** The term a date falls into (or the nearest one), for a school. */
export function useTerms(schoolId: string) {
  const db = useDb();
  return useQuery({
    queryKey: ['terms', schoolId],
    staleTime: 60 * 60_000,
    queryFn: async () =>
      must(
        await db
          .from('terms')
          .select('id, number, starts_on, ends_on')
          .eq('school_id', schoolId)
          .order('starts_on'),
      ),
  });
}

export function termFor<T extends { starts_on: string; ends_on: string }>(
  terms: ReadonlyArray<T>,
  date: string,
): T | undefined {
  return (
    terms.find((t) => t.starts_on <= date && date <= t.ends_on) ??
    [...terms].reverse().find((t) => t.ends_on < date) ??
    terms[0]
  );
}

export async function fetchJournal(db: Db, courseId: string, termId: string): Promise<Journal> {
  const [course, students, lessons, grades, attendance, formative] = await Promise.all([
    db.from('courses').select(COURSE_SELECT).eq('id', courseId).single(),
    db
      .from('course_students')
      .select(
        'student:school_members!course_students_school_id_student_member_id_fkey(id, first_name, last_name)',
      )
      .eq('course_id', courseId),
    db
      .from('lessons')
      .select('id, date, period, topic, bring, is_test, format, status, starts_at, ends_at')
      .eq('course_id', courseId)
      .eq('term_id', termId)
      .order('date')
      .order('period'),
    db.from('grades').select('*').eq('course_id', courseId).eq('term_id', termId),
    db.from('attendance').select('*').eq('course_id', courseId),
    db.from('formative_marks').select('*').eq('course_id', courseId),
  ]);
  return {
    course: toCourseRef(must(course)),
    termId,
    students: must(students)
      .map((r) => ({
        id: r.student.id,
        firstName: r.student.first_name,
        lastName: r.student.last_name,
      }))
      .sort(byName),
    lessons: must(lessons).map(({ is_test, starts_at, ends_at, ...l }) => ({
      ...l,
      isTest: is_test,
      startsAt: starts_at,
      endsAt: ends_at,
    })),
    grades: must(grades),
    attendance: must(attendance),
    formative: must(formative),
  };
}

export function useJournal(courseId: string | undefined, termId: string | undefined) {
  const db = useDb();
  return useQuery({
    queryKey: ['journal', courseId, termId],
    enabled: courseId !== undefined && termId !== undefined,
    queryFn: () => fetchJournal(db, courseId as string, termId as string),
  });
}

/** Which scale a grade of this kind uses in this class (national rules 2026/27). */
export function scaleFor(grade: number, kind: AssessmentKind): ScaleCode {
  return (
    resolveRule(NATIONAL_RULES_2026, { academicYear: '2026/2027', grade, kind })?.scale ??
    'points12'
  );
}

/** Grade columns as stored in the database. */
export function gradeColumns(value: GradeValue | { special: SpecialMark }) {
  const empty = { points: null, level: null, verbal: null, passed: null, special: null };
  if ('special' in value) return { ...empty, special: value.special };
  switch (value.scale) {
    case 'points12':
      return { ...empty, points: value.points };
    case 'levels4':
      return { ...empty, level: value.level };
    case 'verbal':
      return { ...empty, verbal: value.verbal };
    case 'pass_fail':
      return { ...empty, passed: value.passed };
  }
}

/** Back from the database row to a value (null for «н/а» and «звільнений»). */
export function gradeValue(
  row: Pick<GradeRow, 'scale' | 'points' | 'level' | 'verbal' | 'passed'>,
): GradeValue | null {
  switch (row.scale) {
    case 'points12':
      return row.points === null ? null : { scale: 'points12', points: row.points };
    case 'levels4':
      return row.level === null ? null : { scale: 'levels4', level: row.level as 'П' };
    case 'verbal':
      return row.verbal === null ? null : { scale: 'verbal', verbal: row.verbal as 'needs_help' };
    case 'pass_fail':
      return row.passed === null ? null : { scale: 'pass_fail', passed: row.passed };
  }
}

export interface GradeInput {
  schoolId: string;
  courseId: string;
  termId: string;
  studentId: string;
  kind: AssessmentKind;
  scale: ScaleCode;
  lessonId?: string;
  value: GradeValue | { special: SpecialMark };
  title?: string;
  comment?: string;
  /** id of the grade being replaced */
  existingId?: string;
}

export async function saveGrade(db: Db, input: GradeInput): Promise<void> {
  const columns = gradeColumns(input.value);
  if (input.existingId) {
    must(
      await db
        .from('grades')
        .update({ ...columns, comment: input.comment ?? null, title: input.title ?? null })
        .eq('id', input.existingId),
    );
    return;
  }
  must(
    await db.from('grades').insert({
      school_id: input.schoolId,
      course_id: input.courseId,
      term_id: input.termId,
      student_member_id: input.studentId,
      lesson_id: input.lessonId ?? null,
      kind: input.kind,
      scale: input.scale,
      ...columns,
      title: input.title ?? null,
      comment: input.comment ?? null,
    }),
  );
}

export function useJournalActions(courseId: string, termId: string) {
  const db = useDb();
  const client = useQueryClient();
  const invalidate = () => client.invalidateQueries({ queryKey: ['journal', courseId, termId] });
  const action = <A>(fn: (args: A) => Promise<unknown>) =>
    useMutation({ mutationFn: fn, onSettled: invalidate });

  return {
    saveGrade: action((input: GradeInput) => saveGrade(db, input)),
    deleteGrade: action(async (id: string) => must(await db.from('grades').delete().eq('id', id))),
    setAbsence: action(
      async (a: {
        schoolId: string;
        lessonId: string;
        studentId: string;
        mark: AbsenceMark | null;
      }) => {
        if (a.mark === null) {
          return must(
            await db
              .from('attendance')
              .delete()
              .eq('lesson_id', a.lessonId)
              .eq('student_member_id', a.studentId),
          );
        }
        return must(
          await db.from('attendance').upsert(
            {
              school_id: a.schoolId,
              lesson_id: a.lessonId,
              course_id: courseId,
              student_member_id: a.studentId,
              mark: a.mark,
            },
            { onConflict: 'lesson_id,student_member_id' },
          ),
        );
      },
    ),
    setTopic: action(
      async (a: { lessonId: string; topic: string; bring?: string; isTest?: boolean }) =>
        must(
          await db
            .from('lessons')
            .update({
              topic: a.topic || null,
              ...(a.bring !== undefined && { bring: a.bring.trim() || null }),
              ...(a.isTest !== undefined && { is_test: a.isTest }),
            })
            .eq('id', a.lessonId),
        ),
    ),
  };
}

// ---------------------------------------------------------------------------------------------
// Marks that work without connection (docs/plan.md §3 №22, wave 5): the teacher marks a lesson
// cell, the mark waits on the device and goes when the connection is back; a cell that somebody
// else changed meanwhile becomes a conflict the teacher decides.
// ---------------------------------------------------------------------------------------------

const cellOf = (a: { lessonId: string; studentId: string }) => `${a.lessonId}|${a.studentId}`;

/**
 * The marks of a lesson cell, online or not. A second change of the same cell while offline
 * replaces the first in the queue and keeps what the teacher saw before the first one.
 */
export function useJournalMarks() {
  const client = useQueryClient();
  const grade = useMutation<unknown, Error, JournalGradeAction>({
    mutationKey: OFFLINE_ACTIONS.journalGrade,
  });
  const absence = useMutation<unknown, Error, JournalAbsenceAction>({
    mutationKey: OFFLINE_ACTIONS.journalAbsence,
  });
  const replacing = <T extends JournalGradeAction | JournalAbsenceAction>(
    key: readonly string[],
    action: T,
  ): T => {
    const cache = client.getMutationCache();
    const waiting = cache.findAll({
      mutationKey: [...key],
      exact: true,
      predicate: (m) => m.state.isPaused && cellOf(m.state.variables as T) === cellOf(action),
    });
    const first = waiting[0]?.state.variables as T | undefined;
    for (const m of waiting) cache.remove(m);
    return first ? { ...action, seen: first.seen } : action;
  };
  return {
    setGrade: (a: Omit<JournalGradeAction, 'at'>) =>
      grade.mutate(replacing(OFFLINE_ACTIONS.journalGrade, { ...a, at: new Date().toISOString() })),
    setAbsence: (a: Omit<JournalAbsenceAction, 'at'>) =>
      absence.mutate(
        replacing(OFFLINE_ACTIONS.journalAbsence, { ...a, at: new Date().toISOString() }),
      ),
  };
}

/** Marks of a course still waiting for the connection, by cell. */
export function useJournalPending(courseId: string) {
  const grades = useQueuedActions<JournalGradeAction>(OFFLINE_ACTIONS.journalGrade).filter(
    (a) => a.courseId === courseId,
  );
  const absences = useQueuedActions<JournalAbsenceAction>(OFFLINE_ACTIONS.journalAbsence).filter(
    (a) => a.courseId === courseId,
  );
  return {
    grades: new Map(grades.map((a) => [cellOf(a), a])),
    absences: new Map(absences.map((a) => [cellOf(a), a])),
  };
}

export type JournalConflict =
  | {
      id: number;
      kind: 'grade';
      action: JournalGradeAction;
      server: JournalConflictError['server'];
    }
  | {
      id: number;
      kind: 'absence';
      action: JournalAbsenceAction;
      server: JournalConflictError['server'];
    };

/** Cells somebody else changed while the teacher was offline: the teacher decides each. */
export function useJournalConflicts(courseId: string): JournalConflict[] {
  return useMutationState({
    filters: {
      mutationKey: ['offline', 'journal'],
      predicate: (m) =>
        m.state.status === 'error' &&
        isJournalConflict(m.state.error) &&
        (m.state.variables as { courseId: string }).courseId === courseId,
    },
    select: (m) =>
      ({
        id: m.mutationId,
        kind: m.options.mutationKey?.[2] === 'absence' ? 'absence' : 'grade',
        action: m.state.variables,
        server: (m.state.error as JournalConflictError).server,
      }) as JournalConflict,
  });
}

/** «Залишити моє» (sent again over the server's) or «залишити як є» (the teacher's is dropped). */
export function useResolveJournalConflict() {
  const client = useQueryClient();
  const grade = useMutation<unknown, Error, JournalGradeAction>({
    mutationKey: OFFLINE_ACTIONS.journalGrade,
  });
  const absence = useMutation<unknown, Error, JournalAbsenceAction>({
    mutationKey: OFFLINE_ACTIONS.journalAbsence,
  });
  const drop = (id: number) => {
    const cache = client.getMutationCache();
    const m = cache.getAll().find((x) => x.mutationId === id);
    if (m) cache.remove(m);
  };
  return {
    keepMine: (c: JournalConflict) => {
      drop(c.id);
      if (c.kind === 'grade') grade.mutate({ ...c.action, force: true });
      else absence.mutate({ ...c.action, force: true });
    },
    keepTheirs: (c: JournalConflict) => {
      drop(c.id);
      void client.invalidateQueries({ queryKey: ['journal'] });
    },
  };
}
